import { useState } from 'react';
import { LifeBuoy, Plus, Mail, MessageSquare, Inbox, Send, ArrowLeft, ChevronRight } from 'lucide-react';
import type {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketStatus,
} from '@mondivago/types';
import { useSupportTickets } from '@/hooks/support/useSupportTickets';
import { useToast } from '@/hooks/ui/useToast';
import { formatDateDisplay } from '@/shared/lib/formatters';
import { Modal, Button, TextInput, TextArea, Select, Badge, EmptyState, LoadingSpinner } from '@/components/ui';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Read-only contact email pre-filled from the user's profile. */
  contactEmail: string;
}

type View = 'list' | 'new' | 'detail';

const STATUS_LABEL: Record<SupportTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_TONE: Record<SupportTicketStatus, 'secondary' | 'warning' | 'success' | 'gray'> = {
  open: 'secondary',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'gray',
};

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'tour', label: 'Activity / Booking' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function SupportTicketModal({ isOpen, onClose, contactEmail }: SupportTicketModalProps) {
  const { tickets, loading, createTicket } = useSupportTickets();
  const { success: showSuccess, error: showError } = useToast();

  const [view, setView] = useState<View>('list');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('general');
  const [priority, setPriority] = useState<SupportTicketPriority>('normal');
  const [submitting, setSubmitting] = useState(false);
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);

  const canSubmit = subject.trim().length >= 3 && message.trim().length >= 10;

  const handleClose = () => {
    onClose();
    // Reset internal state after a brief delay so it's ready next open.
    setTimeout(() => {
      setView('list');
      setSubject('');
      setMessage('');
      setCategory('general');
      setPriority('normal');
      setActiveTicket(null);
    }, 200);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const result = await createTicket({ subject: subject.trim(), message: message.trim(), category, priority });
    setSubmitting(false);
    if (result.success) {
      showSuccess('Ticket submitted. Our support team will contact you asap via your email.');
      setSubject('');
      setMessage('');
      setCategory('general');
      setPriority('normal');
      setView('list');
    } else {
      showError(result.error ?? 'Unable to submit ticket.');
    }
  };

  // Footer per view
  let footer = (
    <Button onClick={() => setView('new')} icon={Plus} className="w-full sm:w-auto">
      New Ticket
    </Button>
  );
  if (view === 'new') {
    footer = (
      <div className="flex w-full items-center justify-between">
        <Button variant="outline" onClick={() => setView('list')} icon={ArrowLeft}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit || submitting} isLoading={submitting} icon={Send}>
          Submit Ticket
        </Button>
      </div>
    );
  } else if (view === 'detail') {
    footer = (
      <Button variant="outline" onClick={() => setView('list')} icon={ArrowLeft}>
        Back to requests
      </Button>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Support" size="lg" footer={footer}>
      {view === 'list' && (
        <div className="space-y-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title="No support requests"
              description="Need help? Create a ticket and the Mondivago team will get back to you."
              action={
                <Button onClick={() => setView('new')} icon={Plus}>
                  New Ticket
                </Button>
              }
            />
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => {
                    setActiveTicket(ticket);
                    setView('detail');
                  }}
                  className="w-full rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all hover:shadow-md hover:border-gray-200"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-black text-gray-900">{ticket.subject}</p>
                        <Badge variant={STATUS_TONE[ticket.status]} size="sm">
                          {STATUS_LABEL[ticket.status]}
                        </Badge>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-500">{ticket.message}</p>
                      <p className="mt-2 text-xs text-gray-400">
                        {ticket.category} ·{' '}
                        {formatDateDisplay(new Date(ticket.created_at), {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-gray-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'new' && (
        <div className="space-y-6">
          {/* Read-only contact email */}
          <TextInput
            label="Contact Email"
            icon={Mail}
            value={contactEmail}
            disabled
            hint="Our support team will contact you asap via this email. It cannot be changed here."
          />
          <TextInput
            label="Subject"
            icon={MessageSquare}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary of your request"
          />
          <TextArea
            label="Message"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your issue in as much detail as possible..."
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Category"
              value={category}
              onChange={(val) => setCategory(val as SupportTicketCategory)}
              options={CATEGORY_OPTIONS}
            />
            <Select
              label="Priority"
              value={priority}
              onChange={(val) => setPriority(val as SupportTicketPriority)}
              options={PRIORITY_OPTIONS}
            />
          </div>
        </div>
      )}

      {view === 'detail' && activeTicket && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={STATUS_TONE[activeTicket.status]}>{STATUS_LABEL[activeTicket.status]}</Badge>
            <Badge variant="gray">{activeTicket.category}</Badge>
          </div>
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">Your message</p>
            <p className="whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm text-gray-700">{activeTicket.message}</p>
          </div>
          {activeTicket.admin_reply ? (
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-lime-700">Mondivago response</p>
              <p className="whitespace-pre-wrap rounded-xl border border-lime-200 bg-lime-50/60 p-4 text-sm text-gray-700">
                {activeTicket.admin_reply}
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-gray-50 p-4 text-center text-sm text-gray-400">
              <Inbox className="mx-auto mb-2 h-5 w-5 text-gray-300" />
              Our team hasn't responded yet. We usually reply within 24h.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
